import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EmployeePageShell from "./EmployeePageShell";
import useFetch from "../../useFetch";
import { apiRequest } from "../../utils/api";

const money = (amount, currency = "USD") => `${currency} ${Number(amount || 0).toFixed(2)}`;
const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "-";
const formatStatus = (status) => status ? status.replace(/_/g, " ") : "-";

const getSubscriptionEndLabel = (subscription, plan) => {
  if (!subscription?.display_ends_at && !subscription?.ends_at) return "Expiry";
  if (plan?.billing_type !== "subscription") return "Valid until";
  if (subscription?.status === "canceled") return "Ended on";
  return subscription?.cancel_at_period_end ? "Expires on" : "Renews on";
};

const getDaysRemainingText = (subscription) => {
  if (subscription?.days_remaining === null || subscription?.days_remaining === undefined) return "No expiry date";
  if (subscription.days_remaining <= 0) return "Expires today";
  return `${subscription.days_remaining} day${subscription.days_remaining === 1 ? "" : "s"} remaining`;
};

const getCurrentPlanNote = (subscription, plan, endDate) => {
  if (!plan) return "Choose a plan below to add posting access.";
  if (endDate) return `${getSubscriptionEndLabel(subscription, plan)} ${formatDate(endDate)}.`;
  if (plan?.billing_type !== "subscription") return "Credits stay available until they are used.";
  return "No expiry date available.";
};

const EmployeePlans = () => {
  const [apiUrl] = useState(process.env.REACT_APP_BASE_API_URL + "/employer/billing");
  const [invoiceUrl] = useState(process.env.REACT_APP_BASE_API_URL + "/employer/invoices");
  const [billing, setBilling] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const { response, isLoading, error: fetchError } = useFetch(apiUrl);
  const { response: invoicesResponse, isLoading: invoicesLoading, error: invoicesError } = useFetch(invoiceUrl);
  const invoices = invoicesResponse?.invoices ?? [];

  useEffect(() => {
    setBilling(response?.billing ?? null);
  }, [response]);

  const purchasePlan = (plan) => {
    setIsPurchasing(plan.id);
    setSuccess(false);
    setError(false);

    apiRequest(`/employer/billing/plans/${plan.id}/purchase`, {
      method: "POST",
    })
      .then(data => {
        if (data?.checkout?.url) {
          setSuccess("Redirecting to Stripe checkout...");
          window.location.assign(data.checkout.url);
        } else {
          setIsPurchasing(false);
          setError(data?.message ?? "There was some error while activating plan.");
        }
      })
      .catch(error => {
        setIsPurchasing(false);
        setError(error.message);
      });
  };

  const openBillingPortal = () => {
    setIsOpeningPortal(true);
    setSuccess(false);
    setError(false);

    apiRequest("/employer/billing/portal-session", {
      method: "POST",
    })
      .then(data => {
        if (data?.portal?.url) {
          setSuccess("Opening Stripe customer portal...");
          window.location.assign(data.portal.url);
        } else {
          setIsOpeningPortal(false);
          setError(data?.message ?? "Unable to open billing management.");
        }
      })
      .catch(error => {
        setIsOpeningPortal(false);
        setError(error.message);
      });
  };

  const currentSubscription = billing?.current_subscription;
  const currentPlan = currentSubscription?.plan;
  const postingAccess = billing?.posting_access;
  const currentPlanEndDate = currentSubscription?.display_ends_at ?? currentSubscription?.ends_at;
  const currentPlanStartDate = currentSubscription?.display_starts_at ?? currentSubscription?.starts_at;
  const currentDuration = currentSubscription?.duration_days ?? currentPlan?.duration_days;
  const canManageSubscription = currentPlan?.billing_type === "subscription" && currentSubscription?.stripe_subscription_id;

  return (
    <EmployeePageShell
      active="plans"
      title="Plans & Payments"
      text="Manage posting credits, payment history, and billing status."
    >
      {success && <div className="alert alert-success"><strong>{success}</strong></div>}
      {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
      {fetchError && <div className="alert alert-danger"><strong>{fetchError}</strong></div>}
      {invoicesError && <div className="alert alert-danger"><strong>{invoicesError}</strong></div>}
      {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

      <div className="row">
        <div className="col-lg-6 col-md-12">
          <div className="ls-widget employee-plan-widget">
            <div className="widget-title">
              <h4>Current Plan</h4>
            </div>
            <div className="widget-content">
              <div className="employee-plan-card">
                <span>{currentPlan?.name ?? "No active plan"}</span>
                <strong>{currentPlan ? money(currentPlan.price, currentPlan.currency) : "USD 0.00"}</strong>
                <p>{getCurrentPlanNote(currentSubscription, currentPlan, currentPlanEndDate)}</p>
                {currentSubscription && (
                  <div className="employee-plan-details">
                    <div>
                      <span>Status</span>
                      <strong>{formatStatus(currentSubscription.status)}</strong>
                    </div>
                    <div>
                      <span>Started</span>
                      <strong>{formatDate(currentPlanStartDate)}</strong>
                    </div>
                    <div>
                      <span>{getSubscriptionEndLabel(currentSubscription, currentPlan)}</span>
                      <strong>{formatDate(currentPlanEndDate)}</strong>
                    </div>
                    <div>
                      <span>Duration</span>
                      <strong>{currentDuration ? `${currentDuration} days` : "No expiry"}</strong>
                    </div>
                    <div>
                      <span>Time left</span>
                      <strong>{getDaysRemainingText(currentSubscription)}</strong>
                    </div>
                  </div>
                )}
                <Link to="/pricing" className="theme-btn btn-style-one">View Pricing</Link>
                {billing?.latest_invoice && <Link to="/invoice" className="theme-btn btn-style-three" style={{ marginLeft: 12 }}>Invoice</Link>}
                {canManageSubscription && (
                  <button type="button" className="theme-btn btn-style-three" style={{ marginLeft: 12 }} onClick={openBillingPortal} disabled={isOpeningPortal}>
                    {isOpeningPortal ? "Opening..." : "Manage Billing"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-12">
          <div className="ls-widget employee-plan-widget">
            <div className="widget-title">
              <h4>Posting Access</h4>
            </div>
            <div className="widget-content">
              <div className="employee-plan-card">
                <span>Available Posts</span>
                <strong>{postingAccess?.available_job_posts ?? 0} remaining</strong>
                <p>{postingAccess?.credits_added ?? 0} total credits added, {postingAccess?.credits_used ?? 0} used.</p>
                <Link to="/employee-post-job" className="theme-btn btn-style-three">Post a Job</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ls-widget employee-plan-widget">
        <div className="widget-title">
          <h4>Available Plans</h4>
        </div>
        <div className="widget-content">
          <div className="row">
            {(billing?.plans ?? []).map((plan) => (
              <div className="col-lg-4 col-md-6 col-sm-12" key={plan.id}>
                <div className="employee-plan-card employee-plan-option">
                  <span>{plan.name}</span>
                  <strong>{money(plan.price, plan.currency)}</strong>
                  <p>{plan.job_post_limit ?? 1} job post{(plan.job_post_limit ?? 1) === 1 ? "" : "s"} added to your account.</p>
                  <button type="button" className="theme-btn btn-style-one" onClick={() => purchasePlan(plan)} disabled={isPurchasing === plan.id}>
                    {isPurchasing === plan.id ? "Redirecting..." : "Checkout with Stripe"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="ls-widget">
        <div className="widget-title">
          <h4>Purchase History</h4>
          {billing?.latest_invoice && <Link to="/invoice" className="theme-btn btn-style-three">Latest Invoice</Link>}
        </div>
        <div className="widget-content">
          {invoicesLoading && <h5 className="text-center my-3">Loading Invoices</h5>}
          {invoices.length > 0 ? (
            <div className="employee-table-list">
              {invoices.map((invoice) => (
                <div className="employee-table-row employee-invoice-row" key={invoice.id}>
                  <div>
                    <strong>{invoice.invoice_number}</strong>
                    <span>{invoice.item_name} - {invoice.quantity_label ?? "Billing item"} - {formatDate(invoice.paid_at || invoice.created_at)}</span>
                    {invoice.stripe_checkout_session_id && <small>Stripe session: {invoice.stripe_checkout_session_id}</small>}
                  </div>
                  <div className="employee-row-actions">
                    <span className={invoice.status === "failed" ? "danger" : ""}>{invoice.status}</span>
                    <strong>{money(invoice.total, invoice.currency)}</strong>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="employee-empty-state">No invoices yet.</div>
          )}
        </div>
      </div>
    </EmployeePageShell>
  );
};

export default EmployeePlans;
